import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { EmpresaTenant, GrupoEconomico, MatrizPermissao, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import { rotuloUsuarioComNivel } from './rotuloUsuario';
import { alternarTodos, estadoSelecaoTodos, resumoSelecao } from './selecaoUsuarios';
import { filtrarEmpresas, filtrarPorTexto } from './buscaEmpresas';
import { alternarGrupo, empresasDoAcesso } from './selecaoAcessos';
import { destinoAoConcluir, temSelecaoNaoSalva } from './saidaMatrizAcessos';
import { extrairMensagemErro } from './mensagemErro';
import { escaparHtml } from '../clientes/justificativaReset';
import AvisoEmpresasOrfas from './AvisoEmpresasOrfas';
import { usuariosSelecionaveis } from './usuariosSelecionaveis';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaAcessos } from './justificativaSox';
import { formatarCnpj } from '../../utils/String';

/** Campo de pesquisa de um seletor, com botão para limpar. Só filtra a lista exibida; as seleções ficam como estão. */
const CampoBusca: React.FC<{ valor: string; onChange: (v: string) => void; rotulo: string; placeholder: string }> = ({ valor, onChange, rotulo, placeholder }) => (
  <span style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0 6px', fontWeight: 400 }}>
    <input type='search' value={valor} aria-label={rotulo} placeholder={placeholder} style={{ flex: 1 }}
      onChange={e => { const v = e.target.value; onChange(v); }} />
    {valor && <button type='button' className='wf-link' onClick={() => onChange('')}>Limpar</button>}
  </span>
);

const MatrizAcessosPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number } | undefined>();

  const [usuarios, setUsuarios] = useState<UsuarioWorkflow[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [permissoes, setPermissoes] = useState<MatrizPermissao[]>([]);
  const [orfas, setOrfas] = useState<EmpresaTenant[]>([]);
  const [grupos, setGrupos] = useState<GrupoEconomico[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedEmpresas, setSelectedEmpresas] = useState<number[]>([]);
  const [selectedGrupos, setSelectedGrupos] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [filterUser, setFilterUser] = useState('');
  // Pesquisas dos três seletores: só filtram o que é exibido, nunca mexem nas seleções já feitas
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [buscaGrupo, setBuscaGrupo] = useState('');
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  // Justificativa SOX já sugerida (cita usuários e empresas marcados); a pessoa revisa e edita se quiser.
  // Com grupo selecionado não há seleção individual de empresas: vale a composição ATUAL do cadastro (empresa.grupoId).
  const empresasEfetivas = empresasDoAcesso(selectedGrupos, selectedEmpresas, empresas);
  const modoGrupo = selectedGrupos.length > 0;
  const justificativa = useJustificativaSugerida(
    selectedUsers.length > 0 && empresasEfetivas.length > 0
      ? justificativaAcessos({
          usuarios: usuarios.filter(u => selectedUsers.includes(u.id)).map(u => u.nome),
          empresas: empresas.filter(e => empresasEfetivas.includes(e.id)).map(e => e.nome),
        })
      : '');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 5000);
  };

  const fetchDados = useCallback(() => {
    setLoading(true);
    Promise.all([
      workflowService.listarUsuarios(),
      workflowService.listarTenants(),
      workflowService.listarAcessos(),
      workflowService.listarEmpresasOrfas(),
      workflowService.listarGrupos().catch(() => [] as GrupoEconomico[]),
    ])
      .then(([u, e, p, o, g]) => {
        setUsuarios(u);
        setEmpresas(e);
        setPermissoes(p);
        setOrfas(o);
        setGrupos(g);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchDados();
  }, [fetchDados]);

  const salvar = () => {
    if (!justificativa.texto.trim() || !selectedUsers.length || !empresasEfetivas.length) return;
    setSaving(true);
    workflowService
      .concederAcesso({ userIds: selectedUsers, empresaIds: empresasEfetivas, justificativa: justificativa.texto.trim() })
      .then(() => {
        setSelectedUsers([]);
        setSelectedEmpresas([]);
        setSelectedGrupos([]);
        justificativa.restaurar();
        fetchDados();
        showToast('Registro de auditoria SOX gerado com sucesso. Acessos concedidos e log imutável persistido.');
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  // Revogar só acontece depois da confirmação explícita; cancelar (ou fechar a janela) mantém o acesso como está.
  const revogar = async (p: MatrizPermissao) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Revogar acesso?',
      html: `<p style="font-size:14px">O usuário <b>${escaparHtml(p.usuarioNome)}</b> perderá o acesso à empresa <b>${escaparHtml(p.empresaNome)}</b>.</p>` +
        '<p style="font-size:12px;color:#7e8299">A revogação gera registro SOX e não pode ser desfeita por aqui; para devolver o acesso será preciso vinculá-lo de novo.</p>',
      showCancelButton: true,
      confirmButtonText: 'Confirmar revogação',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });
    if (!isConfirmed) return;

    try {
      await workflowService.revogarAcesso(p.usuarioId, p.empresaId);
      fetchDados();
      showToast(`Acesso de ${p.usuarioNome} à empresa ${p.empresaNome} revogado. Registro SOX gerado.`);
    } catch (err) {
      showToast(extrairMensagemErro(err, `Não foi possível revogar o acesso de ${p.usuarioNome} à empresa ${p.empresaNome}.`));
    }
  };

  // Concluído (rodapé): volta ao assistente na etapa de origem sem rolar até o topo. Não salva nada por conta própria: os
  // acessos já gravados ficam como estão, e uma seleção ainda não salva pede confirmação antes de sair.
  const concluir = async () => {
    if (temSelecaoNaoSalva(selectedUsers, empresasEfetivas)) {
      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Há uma seleção não salva',
        text: 'Você marcou usuários ou empresas que ainda não foram vinculados. Sair agora descarta essa seleção.',
        showCancelButton: true,
        confirmButtonText: 'Sair sem salvar',
        cancelButtonText: 'Continuar editando',
        reverseButtons: true,
        focusCancel: true,
      });
      if (!isConfirmed) return;
    }
    const destino = destinoAoConcluir(location.state);
    if (destino.voltar) history.goBack();
    else history.push(destino.path, destino.state);
  };

  const toggleUser = (id: number) =>
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleEmpresa = (id: number) =>
    setSelectedEmpresas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Selecionar um grupo econômico usa os vínculos do cadastro; a escolha individual de empresas fica de fora dessa etapa
  const toggleGrupo = (grupoId: number) => {
    if (!selectedGrupos.includes(grupoId)) setSelectedEmpresas([]);       // ao entrar no modo grupo, a seleção individual sai
    setSelectedGrupos(prev => alternarGrupo(prev, grupoId));
  };

  // "Selecionar todos": só os usuários elegíveis (bloqueados nem aparecem na lista)
  const todosElegiveis = usuariosSelecionaveis(usuarios);
  const usuariosElegiveis = filtrarPorTexto(todosElegiveis, buscaUsuario, u => [u.nome, u.email, u.cargo]);   // os que a pesquisa mostra
  const idsElegiveis = usuariosElegiveis.map(u => u.id);
  const estadoTodos = estadoSelecaoTodos(idsElegiveis, selectedUsers);
  const gruposVisiveis = filtrarPorTexto(grupos, buscaGrupo, g => [g.nome]);
  const empresasVisiveis = filtrarEmpresas(empresas, buscaEmpresa);

  const filteredPermissoes = filterUser
    ? permissoes.filter(p => p.usuarioNome.toLowerCase().includes(filterUser.toLowerCase()))
    : permissoes;

  return (
    <div className='wf-page'>
      <div className='wf-breadcrumb'>Home &rsaquo; Governança de TI &rsaquo; Matriz de Acessos</div>
      <div className='wf-header'>
        <div>
          <h1>Matriz de Acessos — Usuário × Empresa</h1>
          <p>Gestão de permissões N para N com auditoria SOX obrigatória.</p>
        </div>
        <div>
          <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
          <span className='wf-level7' style={{ marginLeft: 10 }}>Acesso exclusivo Nível 7 — TI</span>
        </div>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      <AvisoEmpresasOrfas
        empresas={orfas}
        orientacao='Estas empresas estão sem nenhum usuário vinculado. A TI deve regularizar o acesso com justificativa SOX.'
      />

      {loading ? (
        <div className='wf-count' style={{ textAlign: 'center', padding: 40 }}>Carregando dados...</div>
      ) : (
        <>
          <div className='wf-grid-2' style={{ marginBottom: 0 }}>
            <section className='wf-card'>
              <div className='wf-card__header'>
                <div><h2>Vincular acesso</h2><p>Selecione usuários, empresas e insira justificativa obrigatória.</p></div>
              </div>
              <div className='wf-form-grid wf-form-grid--one'>
                <label>
                  Usuários (multi-seleção)
                  <CampoBusca valor={buscaUsuario} onChange={setBuscaUsuario} rotulo='Pesquisar usuário' placeholder='Pesquisar usuário por nome, e-mail ou cargo...' />
                  <div className='wf-multiselect'>
                    {idsElegiveis.length > 0 && (
                      <label className={`wf-multiselect__item ${estadoTodos === 'todos' ? 'is-selected' : ''}`} style={{ borderBottom: '1px solid #e4e6ef' }}
                        onClick={() => setSelectedUsers(prev => alternarTodos(idsElegiveis, prev))}>
                        <span className='wf-multiselect__check'>{estadoTodos === 'todos' ? '✓' : estadoTodos === 'alguns' ? '–' : ''}</span>
                        <div>
                          <strong>Selecionar todos ({idsElegiveis.length}){buscaUsuario.trim() ? ' da pesquisa' : ''}</strong>
                          <span style={{ display: 'block' }}>{estadoTodos === 'todos' ? 'Clique para desmarcar todos' : 'Marca todos os usuários ativos da lista'}</span>
                        </div>
                      </label>
                    )}
                    {todosElegiveis.length > 0 && usuariosElegiveis.length === 0 && <span className='wf-count'>Nenhum usuário encontrado para a pesquisa.</span>}
                    {usuariosElegiveis.map(u => (
                      <label
                        key={u.id}
                        className={`wf-multiselect__item ${selectedUsers.includes(u.id) ? 'is-selected' : ''}`}
                        onClick={() => toggleUser(u.id)}
                      >
                        <span className='wf-multiselect__check'>{selectedUsers.includes(u.id) ? '✓' : ''}</span>
                        <div><strong>{rotuloUsuarioComNivel(u)}</strong></div>
                      </label>
                    ))}
                  </div>
                  <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#7e8299', fontWeight: 400 }} data-testid='resumo-selecao'>
                    {resumoSelecao(todosElegiveis.map(u => u.id), selectedUsers)}
                    {buscaUsuario.trim() ? ` (pesquisa mostrando ${usuariosElegiveis.length})` : ''}. Usuários bloqueados não aparecem na lista.
                  </span>
                </label>
                <label>
                  Grupo Econômico (usa as empresas vinculadas no cadastro)
                  <CampoBusca valor={buscaGrupo} onChange={setBuscaGrupo} rotulo='Pesquisar grupo econômico' placeholder='Pesquisar grupo econômico...' />
                  <div className='wf-multiselect' style={{ maxHeight: 120 }}>
                    {grupos.length > 0 && gruposVisiveis.length === 0 && <span className='wf-count'>Nenhum grupo econômico encontrado para a pesquisa.</span>}
                    {gruposVisiveis.map(g => {
                      const ids = empresas.filter(e => e.grupoId === g.grupo_id).map(e => e.id);
                      const marcado = selectedGrupos.includes(g.grupo_id);
                      return (
                        <label
                          key={g.grupo_id}
                          className={`wf-multiselect__item ${marcado ? 'is-selected' : ''}`}
                          onClick={() => toggleGrupo(g.grupo_id)}
                        >
                          <span className='wf-multiselect__check'>{marcado ? '✓' : ''}</span>
                          <div><strong>{g.nome}</strong><span>{ids.length} empresa(s)</span></div>
                        </label>
                      );
                    })}
                    {grupos.length === 0 && <span className='wf-count'>Nenhum grupo econômico cadastrado.</span>}
                  </div>
                </label>
                {modoGrupo ? (
                  <div data-testid='empresas-do-grupo' style={{ fontSize: 12, color: '#5e6278' }}>
                    <strong style={{ display: 'block', marginBottom: 4 }}>Empresas dos grupos selecionados ({empresasEfetivas.length})</strong>
                    {empresasEfetivas.length === 0
                      ? <span className='wf-count'>Os grupos selecionados não têm empresas vinculadas.</span>
                      : <ul style={{ margin: 0, paddingLeft: 18 }}>{empresas.filter(e => empresasEfetivas.includes(e.id)).map(e => <li key={e.id}>{e.nome}</li>)}</ul>}
                    <span style={{ display: 'block', marginTop: 6 }}>
                      A composição vem do cadastro de grupos e empresas. Para mudar,{' '}
                      <button type='button' className='wf-link' onClick={() => history.push('/workflow-aprovacao/cadastro-grupos')}>edite o grupo no cadastro</button>
                      {' '}e volte aqui: a lista é atualizada.
                    </span>
                  </div>
                ) : (
                <label>
                    Empresas (multi-seleção)
                    <CampoBusca valor={buscaEmpresa} onChange={setBuscaEmpresa} rotulo='Pesquisar empresa' placeholder='Pesquisar empresa por nome ou CNPJ...' />
                    <div className='wf-multiselect'>
                      {empresas.length > 0 && empresasVisiveis.length === 0 && <span className='wf-count'>Nenhuma empresa encontrada para a pesquisa.</span>}
                      {empresasVisiveis.map(e => (
                        <label
                          key={e.id}
                          className={`wf-multiselect__item ${selectedEmpresas.includes(e.id) ? 'is-selected' : ''} ${e.status === 'ORFA' ? 'is-warning' : ''}`}
                          onClick={() => toggleEmpresa(e.id)}
                        >
                          <span className='wf-multiselect__check'>{selectedEmpresas.includes(e.id) ? '✓' : ''}</span>
                          <div><strong>{e.nome}</strong><span>{e.status === 'ORFA' ? 'Órfã' : formatarCnpj(e.cnpj)}</span></div>
                        </label>
                      ))}
                    </div>
                  </label>
                )}
                <label>
                  Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória)</span>
                  <CampoJustificativaSox
                    justificativa={justificativa}
                    rows={4}
                    placeholder='Descreva o motivo da concessão de acesso para fins de auditoria SOX...'
                  />
                </label>
              </div>
              {selectedUsers.length > 0 && empresasEfetivas.length > 0 && justificativa.texto && (
                <div className='wf-sod' style={{ marginTop: 14 }}>
                  <b>✓</b>
                  <div>
                    <strong>Prévia: {selectedUsers.length} usuário(s) × {empresasEfetivas.length} empresa(s)</strong>
                    <p>Log SOX será gerado automaticamente ao salvar.</p>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  className='wf-btn wf-btn--primary'
                  disabled={!selectedUsers.length || !empresasEfetivas.length || !justificativa.texto.trim() || saving}
                  onClick={salvar}
                >
                  {saving ? 'Salvando...' : 'Salvar e registrar SOX'}
                </button>
              </div>
            </section>

            <section className='wf-card'>
              <div className='wf-card__header'>
                <div><h2>Empresas órfãs</h2><p>Sem usuário vinculado — risco de governança.</p></div>
              </div>
              {orfas.length === 0
                ? <p className='wf-count'>Nenhuma empresa órfã.</p>
                : (
                  <div className='wf-table-wrap'>
                    <table className='wf-table'>
                      <thead><tr><th>Empresa</th><th>CNPJ</th><th>Status</th></tr></thead>
                      <tbody>
                        {orfas.map(e => (
                          <tr key={e.id}>
                            <td><strong className='wf-danger-text'>{e.nome}</strong></td>
                            <td>{formatarCnpj(e.cnpj)}</td>
                            <td><span className='wf-badge wf-badge--rejeitada'>ÓRFÃ</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </section>
          </div>

          <section className='wf-card' style={{ marginTop: 20 }}>
            <div className='wf-card__header'>
              <div><h2>Permissões ativas</h2><p>Matriz atual de acessos vinculados.</p></div>
              <input
                placeholder='Filtrar por usuário...'
                value={filterUser}
                onChange={e => setFilterUser(e.target.value)}
                style={{ minHeight: 36, padding: '6px 10px', border: '1px solid #dfe3ea', borderRadius: 7, fontSize: 12 }}
              />
            </div>
            <div className='wf-table-wrap'>
              <table className='wf-table'>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Cargo / Nível</th>
                    <th>Empresa</th>
                    <th>Concedido por</th>
                    <th>Data</th>
                    <th>Justificativa</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissoes.map(p => {
                    const u = usuarios.find(x => x.id === p.usuarioId);
                    return (
                      <tr key={p.id}>
                        <td><strong>{p.usuarioNome}</strong></td>
                        <td>{u ? (u.nivel ? `N${u.nivel} — ${u.cargo}` : 'Sem nível de aprovação') : '—'}</td>
                        <td>{p.empresaNome}</td>
                        <td>{p.concedidoPor}</td>
                        <td>{p.dataConcessao}</td>
                        <td><span style={{ fontSize: 10 }}>{p.justificativa}</span></td>
                        <td>
                          <button
                            className='wf-btn wf-btn--danger-outline wf-btn--sm'
                            onClick={() => revogar(p)}
                          >
                            Revogar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPermissoes.length === 0 && (
                    <tr><td colSpan={7} className='wf-count'>Nenhuma permissão encontrada.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button className='wf-btn wf-btn--primary' onClick={concluir} data-testid='concluir-matriz-acessos'>Concluído</button>
          </div>
        </>
      )}
    </div>
  );
};

export default MatrizAcessosPage;
